import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingCart, MessageSquare, Truck,
  LogOut, Plus, Trash2, DollarSign, TrendingUp, Loader2, Check, Mail, Send,
  Upload, Download, FileSpreadsheet, Star,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { api, formatMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import OrdersPanel from "./OrdersPanel";
import ReviewsPanel from "./ReviewsPanel";
import { SIZE_CHART_OPTIONS } from "@/components/SizeGuide";

const EMPTY = { name: "", description: "", category: "", price: "", cost: "", image: "", stock: 100, supplier: "", is_bestseller: false, sizes: "", source_url: "" };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [qa, setQa] = useState({ url: "", name: "", cost: "", image: "", markup: 85, sizes: "S, M, L, XL, XXL, 3XL", supplier: "AliExpress", designs: "", category: "Fishing", allows_personalization: false, size_chart: "tops" });
  const [qaImages, setQaImages] = useState("");
  const [quickSaving, setQuickSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [a, p, o, m, s, ab] = await Promise.all([
        api.get("/admin/analytics"), api.get("/admin/products"), api.get("/admin/orders"),
        api.get("/admin/messages"), api.get("/admin/suppliers"), api.get("/admin/abandoned-carts"),
      ]);
      setStats(a.data); setProducts(p.data); setOrders(o.data);
      setMessages(m.data); setSuppliers(s.data); setAbandoned(ab.data);
    } catch {
      toast.error("Could not load dashboard data. Please refresh.");
      setStats((prev) => prev || { revenue: 0, gst_collected: 0, paid_orders: 0, open_messages: 0, products_count: 0, top_products: [], revenue_by_country: {} });
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...p, sizes: (p.sizes || []).join(", "), images: (p.images || []).join("\n") }); setDialogOpen(true); };

  const saveProduct = async () => {
    setSaving(true);
    try {
      const sizes = (form.sizes || "").split(",").map((s) => s.trim()).filter(Boolean);
      const images = (typeof form.images === "string" ? form.images : (form.images || []).join("\n")).split("\n").map((u) => u.trim()).filter(Boolean);
      const designs = (typeof form.designs === "string" ? form.designs : (form.designs || []).join(", ")).split(",").map((d) => d.trim()).filter(Boolean);
      const payload = { ...form, price: parseFloat(form.price), cost: parseFloat(form.cost || 0), stock: parseInt(form.stock || 0), sizes, images, designs, image: form.image || images[0] || "" };
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setDialogOpen(false);
      loadAll();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const deleteProduct = async (id) => {
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    loadAll();
  };

  const resolveMsg = async (id) => {
    await api.put(`/admin/messages/${id}/resolve`);
    loadAll();
  };

  const sendReminder = async (id) => {
    try {
      const { data } = await api.post(`/admin/orders/${id}/remind`);
      toast[data.email_sent ? "success" : "message"](data.email_sent ? "Reminder email sent" : data.note || "Reminder logged (no email key set)");
      if (data.email_sent) loadAll();
    } catch {
      toast.error("Could not send reminder. Please try again.");
    }
  };

  const downloadTemplate = () => {
    const rows = [
      "name,description,category,cost,price,markup_percent,image,stock,supplier,sizes,is_bestseller,source_url",
      'Camo Hunting Shirt,"Breathable long-sleeve camo shirt",Apparel,12.50,,20,https://example.com/shirt.jpg,100,AliExpress - HuntGear Co,"S,M,L,XL,XXL",false,https://www.aliexpress.com/item/1005001234567890.html',
      'Fishing Lure Kit 10pc,"Assorted freshwater lures",Fishing,4.20,14.99,,https://example.com/lures.jpg,200,AliExpress - ProTackle,,true,',
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "gobig-product-import-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Choose a CSV file first"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/admin/products/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      if (data.imported_count > 0) {
        toast.success(`Imported ${data.imported_count} product${data.imported_count === 1 ? "" : "s"}`);
        loadAll();
      } else {
        toast.error("No products imported — check the errors below");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const openImport = () => { setImportFile(null); setImportResult(null); setImportOpen(true); };

  const qaPrice = () => {
    const cost = parseFloat(qa.cost);
    if (!cost || cost <= 0) return null;
    const raw = cost * (1 + (parseFloat(qa.markup) || 0) / 100);
    return Math.max(Math.round(raw) - 0.01, 0.99);
  };

  const quickAdd = async () => {
    const price = qaPrice();
    if (!qa.name.trim()) { toast.error("Paste the product name"); return; }
    if (!price) { toast.error("Enter the AliExpress price (your cost)"); return; }
    setQuickSaving(true);
    const imgList = qaImages.split("\n").map((u) => u.trim()).filter(Boolean);
    try {
      await api.post("/admin/products", {
        name: qa.name.trim(),
        description: `${qa.name.trim()}. Quick-dry performance apparel — shipped worldwide by Go Big Forever.`,
        category: qa.category || "Fishing",
        allows_personalization: qa.allows_personalization,
        size_chart: qa.size_chart || "tops",
        price,
        cost: parseFloat(qa.cost),
        image: imgList[0] || "",
        images: imgList,
        stock: 99,
        supplier: qa.supplier.trim() || "AliExpress",
        is_bestseller: false,
        sizes: qa.sizes.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        designs: (qa.designs || "").split(",").map((d) => d.trim()).filter(Boolean),
        source_url: qa.url.split("?")[0].trim(),
      });
      toast.success(`${qa.name.trim()} added at $${price}`);
      setQa({ url: "", name: "", cost: "", image: "", markup: 85, sizes: "S, M, L, XL, XXL, 3XL", supplier: "AliExpress", designs: "", category: "Fishing", allows_personalization: false });
      setQaImages("");
      setQuickOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add product");
    } finally { setQuickSaving(false); }
  };

  const doLogout = () => { logout(); navigate("/"); };

  if (!stats) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  const chartData = Object.entries(stats.revenue_by_country).map(([k, v]) => ({ name: k, revenue: v }));

  return (
    <div className="min-h-screen bg-secondary">
      {/* Top bar */}
      <div className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-black text-xl tracking-tighter">GO<span className="text-brand">BIG</span>FOREVER</span>
            <span className="text-xs text-white/50 uppercase tracking-widest">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70 hidden sm:inline">{user?.name} · CEO</span>
            <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-1 text-sm hover:text-brand"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview"><LayoutDashboard size={16} className="mr-2" />Overview</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products"><Package size={16} className="mr-2" />Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingCart size={16} className="mr-2" />Orders</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages"><MessageSquare size={16} className="mr-2" />Messages</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews"><Star size={16} className="mr-2" />Reviews</TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="tab-suppliers"><Truck size={16} className="mr-2" />Suppliers</TabsTrigger>
            <TabsTrigger value="carts" data-testid="tab-carts"><Mail size={16} className="mr-2" />Abandoned</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard icon={DollarSign} label="Revenue (paid)" value={formatMoney(stats.revenue)} testid="stat-revenue" />
              <StatCard icon={TrendingUp} label="GST collected" value={formatMoney(stats.gst_collected)} testid="stat-gst" />
              <StatCard icon={ShoppingCart} label="Paid orders" value={stats.paid_orders} testid="stat-orders" />
              <StatCard icon={MessageSquare} label="Open messages" value={stats.open_messages} testid="stat-messages" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-ink/10 rounded-md p-6">
                <h3 className="font-display font-bold mb-4">Revenue by market</h3>
                {chartData.length === 0 ? <p className="text-sm text-muted-foreground">No paid orders yet.</p> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                        {chartData.map((c, i) => <Cell key={c.name} fill={i === 0 ? "#E53935" : "#0A0A0A"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="bg-white border border-ink/10 rounded-md p-6">
                <h3 className="font-display font-bold mb-4">Top sellers</h3>
                <div className="space-y-3">
                  {stats.top_products.map((p) => (
                    <div key={p.id} className="flex items-center gap-3">
                      <img src={p.image} alt="" className="w-10 h-10 object-cover rounded" />
                      <span className="flex-1 text-sm font-medium">{p.name}</span>
                      <span className="text-sm text-muted-foreground">{p.sales_count} sold</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Products */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-xl font-bold">Products ({products.length})</h2>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setQuickOpen(true)} className="btn-primary !py-2 !px-4 text-sm" data-testid="quick-add-btn"><Plus size={16} /> Quick Add</button>
                <button onClick={openImport} className="border border-ink/20 hover:border-brand rounded-md py-2 px-4 text-sm font-medium flex items-center gap-2" data-testid="import-csv-btn"><Upload size={16} /> Import CSV</button>
                <button onClick={openNew} className="border border-ink/20 hover:border-brand rounded-md py-2 px-4 text-sm font-medium flex items-center gap-2" data-testid="add-product-btn"><Plus size={16} /> Full form</button>
              </div>
            </div>
            <div className="bg-white border border-ink/10 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-left"><tr>
                  <th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Price</th>
                  <th className="p-3">Margin</th><th className="p-3">Stock</th><th className="p-3">Sold</th><th className="p-3"></th>
                </tr></thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-ink/5" data-testid={`admin-product-${p.id}`}>
                      <td className="p-3 flex items-center gap-2"><img src={p.image} alt="" className="w-9 h-9 object-cover rounded" /><span className="font-medium">{p.name}</span></td>
                      <td className="p-3">{p.category}</td>
                      <td className="p-3 font-mono">{formatMoney(p.price)}</td>
                      <td className="p-3 text-green-700 font-medium">{p.cost ? `${Math.round((1 - p.cost / p.price) * 100)}%` : "—"}</td>
                      <td className="p-3">{p.stock}</td>
                      <td className="p-3">{p.sales_count}</td>
                      <td className="p-3 flex gap-2">
                        {p.source_url && (
                          <a href={p.source_url} target="_blank" rel="noreferrer" className="text-xs underline text-blue-700" data-testid={`source-link-${p.id}`}>Supplier ↗</a>
                        )}
                        <button onClick={() => openEdit(p)} className="text-xs underline" data-testid={`edit-product-${p.id}`}>Edit</button>
                        <button onClick={() => deleteProduct(p.id)} className="text-brand" data-testid={`delete-product-${p.id}`}><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders">
            <OrdersPanel orders={orders} products={products} reload={loadAll} />
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsPanel />
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages">
            <h2 className="font-display text-xl font-bold mb-4">Support messages</h2>
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="bg-white border border-ink/10 rounded-md p-4" data-testid={`admin-message-${m.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{m.name}</span>
                      <span className="text-xs uppercase bg-ink text-white px-2 py-0.5">{m.role}</span>
                      <span className="text-xs text-muted-foreground">{m.email}</span>
                    </div>
                    {m.status === "open" ? (
                      <button onClick={() => resolveMsg(m.id)} className="text-xs flex items-center gap-1 text-green-700" data-testid={`resolve-${m.id}`}><Check size={14} /> Resolve</button>
                    ) : <span className="text-xs text-muted-foreground">Resolved</span>}
                  </div>
                  {m.subject && <p className="text-sm font-medium">{m.subject}</p>}
                  <p className="text-sm text-muted-foreground">{m.message}</p>
                </div>
              ))}
              {messages.length === 0 && <p className="text-muted-foreground">No messages yet.</p>}
            </div>
          </TabsContent>

          {/* Suppliers */}
          <TabsContent value="suppliers">
            <h2 className="font-display text-xl font-bold mb-4">Suppliers & credit terms</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.map((s) => (
                <div key={s.id} className="bg-white border border-ink/10 rounded-md p-5" data-testid={`supplier-${s.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-bold">{s.name}</h3>
                    <span className="text-xs font-bold bg-brand text-white px-2 py-1">NET {s.credit_terms_days}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">{s.product_category}</p>
                  <p className="text-xs text-muted-foreground">{s.contact_email}</p>
                  <p className="text-xs mt-2">{s.notes}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Abandoned carts */}
          <TabsContent value="carts">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">Abandoned carts ({abandoned.length})</h2>
              <p className="text-xs text-muted-foreground">Checkouts started but not paid (20+ min). Send a reminder with a discount to recover the sale.</p>
            </div>
            <div className="bg-white border border-ink/10 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-left"><tr>
                  <th className="p-3">Customer</th><th className="p-3">Items</th><th className="p-3">Value</th>
                  <th className="p-3">Started</th><th className="p-3">Reminder</th>
                </tr></thead>
                <tbody>
                  {abandoned.map((o) => (
                    <tr key={o.id} className="border-t border-ink/5" data-testid={`abandoned-${o.id}`}>
                      <td className="p-3">{o.customer?.name}<br /><span className="text-xs text-muted-foreground">{o.customer?.email}</span></td>
                      <td className="p-3 text-xs">{o.items?.reduce((s, i) => s + i.quantity, 0)} item(s)</td>
                      <td className="p-3 font-mono">{formatMoney(o.total, o.currency)}</td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="p-3">
                        <button onClick={() => sendReminder(o.id)} data-testid={`remind-${o.id}`} className="text-xs flex items-center gap-1 bg-ink text-white px-3 py-1.5 hover:bg-brand transition-colors">
                          <Send size={13} /> {o.reminder_sent_at ? "Resend" : "Remind"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {abandoned.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No abandoned carts 🎉</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Product dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-md border border-ink/10 shadow-xl">
          <DialogHeader><DialogTitle>{editing ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="form-name" />
            <Field label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="form-category" />
            <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="form-description" textarea />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="form-price" />
              <Field label="Cost" type="number" value={form.cost} onChange={(v) => setForm({ ...form, cost: v })} testid="form-cost" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Stock" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} testid="form-stock" />
              <Field label="Supplier" value={form.supplier} onChange={(v) => setForm({ ...form, supplier: v })} testid="form-supplier" />
            </div>
            <Field label="Image URL" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="form-image" />
            <Field label="Sizes (comma-separated, e.g. S, M, L, XL)" value={form.sizes} onChange={(v) => setForm({ ...form, sizes: v })} testid="form-sizes" />
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-ink/70">Size chart</label>
              <select value={form.size_chart || "tops"} onChange={(e) => setForm({ ...form, size_chart: e.target.value })} data-testid="form-size-chart"
                className="w-full bg-white border border-ink/25 focus:border-brand outline-none rounded-md p-2.5 mt-1 text-sm">
                {SIZE_CHART_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <Field label="Supplier link (AliExpress URL, used by the Fulfil panel)" value={form.source_url || ""} onChange={(v) => setForm({ ...form, source_url: v })} testid="form-source-url" />
            <Field label="Extra photo links (one per line)" textarea value={typeof form.images === "string" ? form.images : (form.images || []).join("\n")} onChange={(v) => setForm({ ...form, images: v })} testid="form-images" />
            <Field label="Designs (comma-separated, e.g. Blue Marlin, Teal Camo — buyers must pick one)" value={typeof form.designs === "string" ? form.designs : (form.designs || []).join(", ")} onChange={(v) => setForm({ ...form, designs: v })} testid="form-designs" />
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={!!form.allows_personalization} onChange={(e) => setForm({ ...form, allows_personalization: e.target.checked })} data-testid="form-personalization" className="h-4 w-4 accent-[#E53935]" />
              Buyers can add a printed name
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_bestseller} onChange={(e) => setForm({ ...form, is_bestseller: e.target.checked })} data-testid="form-bestseller" /> Mark as best seller
            </label>
          </div>
          <DialogFooter>
            <button onClick={saveProduct} disabled={saving} className="btn-primary w-full !py-2" data-testid="save-product-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : "Save product"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white" data-testid="import-csv-dialog">
          <DialogHeader><DialogTitle>Bulk import products (CSV)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-secondary rounded-md p-4 text-sm space-y-2">
              <p className="font-medium flex items-center gap-2"><FileSpreadsheet size={16} className="text-brand" /> How it works</p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1 text-xs">
                <li>Fill in the template — one product per row.</li>
                <li>Leave <strong>price</strong> blank and set <strong>markup_percent</strong> (e.g. 20) to auto-price from cost.</li>
                <li><strong>sizes</strong>: comma-separated, e.g. "S,M,L,XL,XXL". Leave blank for non-apparel.</li>
                <li>Duplicate product names are skipped automatically.</li>
              </ul>
              <button onClick={downloadTemplate} className="text-brand text-xs font-medium underline flex items-center gap-1" data-testid="download-template-btn">
                <Download size={13} /> Download CSV template
              </button>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">CSV file</label>
              <input type="file" accept=".csv" data-testid="csv-file-input"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                className="w-full mt-1 text-sm file:mr-3 file:btn-primary file:!py-1.5 file:!px-3 file:border-0 file:text-xs file:cursor-pointer" />
            </div>
            {importResult && (
              <div className="text-sm space-y-2" data-testid="import-result">
                <p className="font-medium text-green-700">✓ {importResult.imported_count} imported · {importResult.skipped_count} skipped</p>
                {importResult.imported.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border border-ink/10 rounded-md text-xs">
                    {importResult.imported.map((p) => (
                      <div key={p.name} className="flex justify-between px-3 py-1.5 border-b border-ink/5 last:border-0">
                        <span>{p.name}</span>
                        <span className="font-mono">{formatMoney(p.price)}{p.margin_pct != null && <span className="text-green-700 ml-2">{p.margin_pct}% margin</span>}</span>
                      </div>
                    ))}
                  </div>
                )}
                {importResult.errors.length > 0 && (
                  <div className="max-h-28 overflow-y-auto bg-red-50 rounded-md p-2 text-xs text-red-700 space-y-1" data-testid="import-errors">
                    {importResult.errors.map((e, i) => <p key={`${i}-${e.slice(0, 24)}`}>{e}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={runImport} disabled={importing || !importFile} className="btn-primary w-full !py-2 disabled:opacity-50" data-testid="run-import-btn">
              {importing ? <Loader2 size={16} className="animate-spin" /> : <><Upload size={16} /> Import products</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add dialog — mobile-first */}
      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto bg-white rounded-md" aria-describedby={undefined} data-testid="quick-add-dialog">
          <DialogHeader><DialogTitle>Quick Add from AliExpress</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">1 · AliExpress link</label>
              <input value={qa.url} onChange={(e) => setQa({ ...qa, url: e.target.value })} placeholder="Paste from the address bar"
                inputMode="url" data-testid="qa-url"
                className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">2 · Product name</label>
              <input value={qa.name} onChange={(e) => setQa({ ...qa, name: e.target.value })} placeholder="Paste or type the title"
                data-testid="qa-name"
                className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">3 · Their price (AU$)</label>
                <input value={qa.cost} onChange={(e) => setQa({ ...qa, cost: e.target.value })} placeholder="11.79"
                  inputMode="decimal" data-testid="qa-cost"
                  className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Markup %</label>
                <input value={qa.markup} onChange={(e) => setQa({ ...qa, markup: e.target.value })}
                  inputMode="numeric" data-testid="qa-markup"
                  className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base" />
              </div>
            </div>
            {qaPrice() && (
              <div className="bg-secondary rounded-md p-3 text-sm flex justify-between items-center" data-testid="qa-price-preview">
                <span>Your selling price:</span>
                <span className="font-display font-black text-lg">${qaPrice()}
                  <span className="text-xs font-normal text-green-700 ml-2">
                    ${(qaPrice() - parseFloat(qa.cost)).toFixed(2)} profit
                  </span>
                </span>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">4 · Photo links — one per line, first is the main photo (right-click each photo → copy image link)</label>
              <textarea value={qaImages} onChange={(e) => setQaImages(e.target.value)} placeholder={"https://ae01.alicdn.com/main.jpg\nhttps://ae01.alicdn.com/back.jpg\nhttps://ae01.alicdn.com/detail.jpg"}
                rows={3} data-testid="qa-image"
                className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base" />
              <div className="flex gap-2 mt-2 flex-wrap">
                {qaImages.split("\n").map((u) => u.trim()).filter(Boolean).slice(0, 5).map((u, i) => (
                  <img key={u} src={u} alt={`preview ${i + 1}`} className={`h-16 w-16 object-cover rounded-md border ${i === 0 ? "border-brand border-2" : "border-ink/10"}`}
                    onError={(e) => { e.target.style.display = "none"; }} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Sizes</label>
                <input value={qa.sizes} onChange={(e) => setQa({ ...qa, sizes: e.target.value })} data-testid="qa-sizes"
                  className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Supplier</label>
                <input value={qa.supplier} onChange={(e) => setQa({ ...qa, supplier: e.target.value })} data-testid="qa-supplier"
                  className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Designs — optional, comma-separated (buyers must pick one, e.g. Blue Marlin, Teal Camo)</label>
              <input value={qa.designs} onChange={(e) => setQa({ ...qa, designs: e.target.value })} data-testid="qa-designs"
                className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base" />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Category</label>
                <select value={qa.category} onChange={(e) => setQa({ ...qa, category: e.target.value })} data-testid="qa-category"
                  className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base bg-white">
                  {["Fishing", "Hunting", "Apparel", "Accessories", "General"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground">Size chart</label>
                <select value={qa.size_chart} onChange={(e) => setQa({ ...qa, size_chart: e.target.value })} data-testid="qa-size-chart"
                  className="w-full border border-ink/20 focus:border-brand outline-none rounded-md p-3 mt-1 text-base bg-white">
                  {SIZE_CHART_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={qa.allows_personalization} onChange={(e) => setQa({ ...qa, allows_personalization: e.target.checked })} data-testid="qa-personalization" className="h-4 w-4 accent-[#E53935]" />
              Buyers can add a printed name
            </label>
          </div>
          <DialogFooter>
            <button onClick={quickAdd} disabled={quickSaving} className="btn-primary w-full !py-3 disabled:opacity-50" data-testid="qa-submit-btn">
              {quickSaving ? <Loader2 size={16} className="animate-spin" /> : "Add to store"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-ink/10 rounded-md p-5" data-testid={testid}>
    <Icon className="text-brand mb-3" size={22} />
    <p className="text-2xl font-display font-black">{value}</p>
    <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
  </div>
);

const Field = ({ label, value, onChange, type = "text", testid, textarea }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wide text-ink/70">{label}</label>
    {textarea ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-white border border-ink/25 focus:border-brand outline-none rounded-md p-2.5 mt-1 text-sm" rows={2} />
    ) : (
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-white border border-ink/25 focus:border-brand outline-none rounded-md p-2.5 mt-1 text-sm" />
    )}
  </div>
);
